/**
 * Comment store. Comments are "stored" next to the reader, which is to
 * say: nowhere. The canned threads ship with the page so the front page
 * never looks empty on slow universes.
 */

export interface Comment {
  id: string;
  author: string;
  universe: string;
  ts: number;
  body: string;
  status: "visible" | "held" | "retired";
  replies?: Comment[];
}

const MIN = 60_000;

/** Canned threads for the three front-page stories. */
export const CANNED: Record<string, Comment[]> = {
  "claude-code-map": [
    {
      id: "c-2041",
      author: "fuzzcademy",
      universe: "A-1",
      ts: Date.now() - 214 * MIN,
      body: "They spent three years writing an Undercover Mode to keep secrets out of public repos and then shipped the repo. Poetic. Sending this to my config management team with no comment.",
      status: "visible",
      replies: [
        {
          id: "c-2044",
          author: "j. (desk)",
          universe: "A-1",
          ts: Date.now() - 198 * MIN,
          body: "We have been asked not to gloat. We are not gloating. We are taking notes for a feature story.",
          status: "visible",
        },
      ],
    },
    {
      id: "c-2052",
      author: "b11_correspondent",
      universe: "B-11",
      ts: Date.now() - 160 * MIN,
      body: "In our universe the map shipped AND the zip bucket was public. Same week. We do not recommend the pace.",
      status: "visible",
    },
    {
      id: "c-2060",
      author: "quiet_part_out_loud",
      universe: "A-1",
      ts: Date.now() - 88 * MIN,
      body: "DMCA'd 8,100 repos, narrowed to 96. Somewhere a paralegal is breathing into a paper bag.",
      status: "visible",
    },
  ],
  "b11-bureau": [
    {
      id: "c-2071",
      author: "adjacent_reader",
      universe: "B-11",
      ts: Date.now() - 95 * MIN,
      body: "For the record, our ordinary engineering was here first by four days. The memo is identical because it is the same memo. We both cite the same memo.",
      status: "visible",
    },
    {
      id: "c-2073",
      author: "desk_intern",
      universe: "A-1",
      ts: Date.now() - 61 * MIN,
      body: "Editorial has declined to arbitrate. Editorial has also declined to open the door between the bureaus. Both declines are permanent.",
      status: "visible",
    },
  ],
  "c4-capsule": [
    {
      id: "c-2081",
      author: "chronically_online",
      universe: "C-4",
      ts: Date.now() - 45 * MIN,
      body: "It contains next Tuesday's corrections page. The desk refuses to publish it, which is the most journalistic thing I have ever seen.",
      status: "visible",
      replies: [
        {
          id: "c-2083",
          author: "j. (desk)",
          universe: "A-1",
          ts: Date.now() - 40 * MIN,
          body: "We publish corrections AFTER the mistake, as tradition demands.",
          status: "visible",
        },
      ],
    },
  ],
};

export function threadFor(storyId: string): Comment[] {
  return CANNED[storyId] ?? [];
}

/** Local "posted" comments persist for the session and then do not. */
const posted: Comment[] = [];

export function postLocal(storyId: string, author: string, body: string): Comment {
  const c: Comment = {
    id: `local-${posted.length + 1}`,
    author,
    universe: document.documentElement.dataset.universe ?? "A-1",
    ts: Date.now(),
    body,
    status: "held",
  };
  posted.push(c);
  return c;
}

export function postedFor(storyId: string): Comment[] {
  // one thread per story, kept in memory only. refresh forgives all.
  return posted.filter((c) => (c as Comment & { story?: string }).story === storyId);
}
